import { z } from 'zod';
/**
 * The label data model (B2).
 *
 * Everything downstream reads this schema, and the one thing that cannot be recovered later is
 * information it never recorded. So the shapes here are deliberately wider than what the first
 * screen needs: `chart_config` because "why did I call that a vwap_reclaim" is answerable in
 * six months only if what was on screen was written down, `decided_at` because replay's cursor
 * and the entry bar are different moments and reconstructing the distinction afterwards from
 * `mode` is guesswork, `confidence` because how sure you were at the time is a variable and not
 * a footnote.
 */
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD')
    .refine((s) => !Number.isNaN(Date.parse(`${s}T00:00:00Z`)), 'date must be a real date');
const instant = z
    .string()
    .datetime({ offset: true })
    .describe('RFC 3339 instant, UTC or with an offset');
const symbol = z
    .string()
    .regex(/^[A-Z][A-Z0-9.]{0,9}$/, 'symbol must be 1–10 upper-case letters/digits/dots');
/**
 * The controlled vocabulary. Closed, validated at the API, and extended only by migration.
 *
 * Free-form tags become garbage within a week — "orb", "ORB", "opening range breakout",
 * "orb?" — and there is no repairing them afterwards, because the distinction between two
 * spellings and two intentions is gone. A closed list is annoying exactly once, at the moment
 * something does not fit, and that moment is worth a migration.
 *
 * `other` exists so that the annoyance has a pressure valve that is visible in the data: a
 * rising count of `other` is the signal that the vocabulary needs extending, and it is
 * countable in a way that a free-text field never is.
 */
export const SETUP_TAGS = [
    'orb_breakout',
    'vwap_reclaim',
    'vwap_rejection',
    'trend_pullback',
    'range_fade',
    'gap_fill',
    'momentum_continuation',
    'reversal_exhaustion',
    'news_driven',
    'other',
];
export const setupTagSchema = z.enum(SETUP_TAGS);
export const LABEL_MODES = ['hindsight', 'replay'];
export const labelModeSchema = z.enum(LABEL_MODES);
export const LABEL_DIRECTIONS = ['long', 'short'];
export const labelDirectionSchema = z.enum(LABEL_DIRECTIONS);
/** Where a feature vector was sampled. C3 adds outcomes; B2 only defines the table. */
export const FEATURE_POINTS = ['entry', 'exit'];
/**
 * The schema version of a `trade_label` ROW — distinct from `indicator_version`, which
 * versions the features, and from the migration number, which versions the table.
 *
 * It exists so that a row written under an older shape can be read alongside a newer one
 * without either being rewritten. Bump it when the MEANING of a field changes, not when a
 * column is added: a nullable addition is readable by both.
 */
export const LABEL_SCHEMA_VERSION = 1;
/**
 * What was on screen while the label was made.
 *
 * Open-ended on purpose — B1 owns the chart and will grow overlays this package should not
 * have to know about. The three fields named here are the ones an analysis will want to group
 * by; anything else B1 puts in the object is carried along unexamined.
 */
export const chartConfigSchema = z
    .object({
    timeframe: z.string().optional(),
    indicators: z.array(z.string()).optional(),
    lowerPane: z.string().nullable().optional(),
})
    .passthrough();
export const createLabelSessionSchema = z
    .object({
    mode: labelModeSchema,
    symbol,
    rangeStart: isoDate,
    rangeEnd: isoDate,
    timeframe: z.string().min(1).max(16).default('5m'),
    chartConfig: chartConfigSchema.default({}),
    notes: z.string().max(4000).optional(),
})
    .refine((s) => s.rangeStart <= s.rangeEnd, {
    message: 'rangeStart must not be after rangeEnd',
    path: ['rangeEnd'],
});
/**
 * A label as written. `labelerId` is deliberately absent: it comes from the authenticated
 * session and never from the body, because a labeller's identity is one of the variables this
 * dataset is measuring and a client that can assert it can corrupt that measurement.
 */
export const createTradeLabelSchema = z
    .object({
    sessionId: z.string().uuid(),
    symbol,
    direction: labelDirectionSchema,
    entryTs: instant,
    entryPrice: z.string().regex(/^-?\d+(\.\d+)?$/, 'price must be a decimal string'),
    exitTs: instant,
    exitPrice: z.string().regex(/^-?\d+(\.\d+)?$/, 'price must be a decimal string'),
    mode: labelModeSchema,
    confidence: z.number().int().min(1).max(5),
    setupTag: z.array(setupTagSchema).min(1).max(4),
    rationale: z.string().max(4000).optional(),
    /**
     * Replay: where the cursor sat when the decision was made. Hindsight: equal to `entryTs`,
     * and the API fills it in rather than making every caller repeat it.
     */
    decidedAt: instant.optional(),
    /**
     * Required to accept a session whose coverage is below the gate, and recorded. A 5-minute
     * chart built from 87% of its minutes looks completely normal, which is exactly why the
     * refusal has to be explicit to override rather than a warning to scroll past.
     */
    coverageOverrideReason: z.string().min(10).max(500).optional(),
})
    .refine((l) => l.exitTs > l.entryTs, {
    message: 'exitTs must be after entryTs',
    path: ['exitTs'],
});
/** A correction. The body is a complete replacement label, not a patch. */
export const supersedeLabelSchema = z.object({
    replacement: createTradeLabelSchema,
    reason: z.string().min(3).max(500),
});
export const listLabelsQuerySchema = z
    .object({
    symbol: symbol.optional(),
    mode: labelModeSchema.optional(),
    tag: setupTagSchema.optional(),
    sessionId: z.string().uuid().optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
    /**
     * The current label set AS OF this instant — what it looked like then, not what it looks
     * like now. Without this, a supersession silently rewrites the history of every analysis
     * that ran before it, and "the model was trained on these labels" stops being checkable.
     */
    asOf: instant.optional(),
    /** Include superseded rows. Off by default: the current set is what almost every read wants. */
    includeSuperseded: z.coerce.boolean().default(false),
    limit: z.coerce.number().int().min(1).max(500).default(100),
    offset: z.coerce.number().int().min(0).default(0),
})
    .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: 'from must not be after to',
    path: ['to'],
});
export const tradeLabelSchema = z.object({
    id: z.string().uuid(),
    sessionId: z.string().uuid(),
    labelerId: z.string().uuid(),
    symbol: z.string(),
    sessionDate: isoDate,
    direction: labelDirectionSchema,
    entryTs: z.string(),
    entryPrice: z.string(),
    exitTs: z.string(),
    exitPrice: z.string(),
    mode: labelModeSchema,
    confidence: z.number().int(),
    setupTag: z.array(setupTagSchema),
    rationale: z.string().nullable(),
    decidedAt: z.string(),
    supersededBy: z.string().uuid().nullable(),
    supersedeReason: z.string().nullable(),
    /** Withdrawal, as distinct from correction: no replacement, and the row stays. */
    retractedAt: z.string().nullable(),
    retractReason: z.string().nullable(),
    coverageOverrideReason: z.string().nullable(),
    createdAt: z.string(),
    schemaVersion: z.number().int(),
});
export const labelSessionSchema = z.object({
    id: z.string().uuid(),
    labelerId: z.string().uuid(),
    mode: labelModeSchema,
    symbol: z.string(),
    rangeStart: isoDate,
    rangeEnd: isoDate,
    timeframe: z.string(),
    chartConfig: chartConfigSchema,
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    notes: z.string().nullable(),
});
export const labelListResponseSchema = z.object({
    items: z.array(tradeLabelSchema),
    total: z.number().int().nonnegative(),
    limit: z.number().int(),
    offset: z.number().int(),
    /** Echoed so a caller can tell a point-in-time read from a current one in its own logs. */
    asOf: z.string().nullable(),
});
// ---- B3: retraction, skips and the session queue ----------------------------------------
/**
 * Withdrawing a label, as distinct from correcting one.
 *
 * A correction has a replacement; a withdrawal does not. Both leave the original row in place,
 * because "I labelled this and then decided it was wrong" is a fact about the labeller and
 * deleting the row would destroy it — arguably the most informative fact available, since it is
 * the one where the judgement changed completely rather than by a bar or two.
 */
export const retractLabelSchema = z.object({
    reason: z.string().min(3).max(500),
});
/**
 * Skip reasons. Closed, like the setup vocabulary, because the three mean genuinely different
 * things downstream and free text would collapse that distinction within a week.
 *
 *  - `no_setup` is a NEGATIVE SAMPLE: a session inspected and declined.
 *  - `unclear` is an abstention and must not be trained on as either class.
 *  - `data_looks_wrong` is a bug report about the feed that arrives through the labelling UI.
 */
export const SKIP_REASONS = ['no_setup', 'unclear', 'data_looks_wrong'];
export const skipReasonSchema = z.enum(SKIP_REASONS);
export const createSkipSchema = z.object({
    symbol: z.string().regex(/^[A-Z][A-Z0-9.]{0,9}$/),
    sessionDate: isoDate,
    reason: skipReasonSchema,
    notes: z.string().max(500).optional(),
});
export const labelQueueDefinitionSchema = z
    .object({
    symbols: z
        .array(z.string().regex(/^[A-Z][A-Z0-9.]{0,9}$/))
        .min(1)
        .max(12),
    rangeStart: isoDate,
    rangeEnd: isoDate,
    includeBelowGate: z.boolean().default(false),
})
    .refine((q) => q.rangeStart <= q.rangeEnd, {
    message: 'rangeStart must not be after rangeEnd',
    path: ['rangeEnd'],
});
export const QUEUE_ENTRY_STATES = ['pending', 'labelled', 'skipped', 'below_gate'];
export const queueEntrySchema = z.object({
    symbol: z.string(),
    sessionDate: isoDate,
    state: z.enum(QUEUE_ENTRY_STATES),
    labels: z.number().int().nonnegative(),
    skipReason: skipReasonSchema.nullable(),
    /** Regular-hours coverage as a decimal string, or null when no coverage row exists. */
    coverage: z.string().nullable(),
});
export const labelQueueSchema = z.object({
    definition: labelQueueDefinitionSchema.nullable(),
    /**
     * Every session in the range for every symbol, in order, with its state. The client derives
     * "next" and "previous" from this rather than asking the server to remember a cursor: a
     * stored position drifts the moment a second sitting or a retraction writes anything.
     */
    entries: z.array(queueEntrySchema),
    /** The first `pending` entry, or null when the queue is finished. */
    resumeAt: queueEntrySchema.nullable(),
    progress: z.object({
        total: z.number().int().nonnegative(),
        labelled: z.number().int().nonnegative(),
        skipped: z.number().int().nonnegative(),
        pending: z.number().int().nonnegative(),
        /**
         * Sessions held back for coverage. Shown rather than silently dropped: hours spent
         * labelling holes are unrecoverable, and the count is the only sign they exist.
         */
        belowGate: z.number().int().nonnegative(),
        labels: z.number().int().nonnegative(),
    }),
});
/** Running counters, so a lopsided dataset is noticed while it can still be fixed. */
export const labelStatsSchema = z.object({
    total: z.number().int().nonnegative(),
    bySymbol: z.record(z.string(), z.number().int()),
    byTag: z.record(z.string(), z.number().int()),
    byDirection: z.record(z.string(), z.number().int()),
    byMode: z.record(z.string(), z.number().int()),
    retracted: z.number().int().nonnegative(),
    superseded: z.number().int().nonnegative(),
    skips: z.record(z.string(), z.number().int()),
});
//# sourceMappingURL=labels.js.map